/**
 * Publish popover (Section 4 wireframe):
 *   ( ) Private — only me
 *   (•) Published to my org
 *       [✓] Show my name on this workflow      (attributionVisible)
 *   ( ) Org standard — push to everyone        (admin only)
 *
 * Closes on outside click / Escape. Calls onPublish with the chosen
 * visibility + attributionVisible.
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { WorkflowVisibility } from '@mana/shared';
import { Button } from '../../ui/Button.js';

export interface PublishChoice {
  visibility: WorkflowVisibility;
  attributionVisible: boolean;
}

export function PublishPopover({
  currentVisibility,
  currentAttribution,
  isAdmin,
  disabled,
  onPublish,
  isPending,
}: {
  currentVisibility: WorkflowVisibility;
  currentAttribution: boolean;
  isAdmin: boolean;
  disabled?: boolean;
  onPublish: (choice: PublishChoice) => void;
  isPending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [visibility, setVisibility] = useState<WorkflowVisibility>(currentVisibility);
  const [attributionVisible, setAttributionVisible] = useState<boolean>(currentAttribution);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync local state when external props change (e.g. after publish).
  useEffect(() => {
    setVisibility(currentVisibility);
    setAttributionVisible(currentAttribution);
  }, [currentVisibility, currentAttribution]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <Button
        variant="primary"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Publish
        <svg viewBox="0 0 10 6" aria-hidden className="ml-1 h-2 w-2">
          <path d="M0 0 L5 6 L10 0" fill="currentColor" />
        </svg>
      </Button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-80 rounded-xl border border-slate-200 bg-white p-4 shadow-floating">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Visibility
          </p>
          <fieldset className="space-y-2 text-sm">
            <RadioRow
              checked={visibility === 'PRIVATE'}
              onChange={() => setVisibility('PRIVATE')}
              label="Private — only me"
            />
            <div>
              <RadioRow
                checked={visibility === 'ORG_PUBLISHED'}
                onChange={() => setVisibility('ORG_PUBLISHED')}
                label="Published to my org"
              />
              {visibility === 'ORG_PUBLISHED' && (
                <label className="ml-6 mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={attributionVisible}
                    onChange={(e) => setAttributionVisible(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
                  />
                  Show my name on this workflow
                </label>
              )}
            </div>
            <RadioRow
              checked={visibility === 'ADMIN_PUBLISHED'}
              onChange={() => isAdmin && setVisibility('ADMIN_PUBLISHED')}
              label={
                <span
                  className={clsx(
                    'flex items-center gap-2',
                    !isAdmin && 'text-slate-400',
                  )}
                >
                  Org standard — push to everyone
                  {!isAdmin && (
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                      admin only
                    </span>
                  )}
                </span>
              }
              disabled={!isAdmin}
            />
          </fieldset>

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="gold"
              size="sm"
              disabled={isPending}
              onClick={() => {
                onPublish({ visibility, attributionVisible });
                setOpen(false);
              }}
            >
              {isPending ? 'Publishing…' : 'Publish'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RadioRow({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  label: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={clsx(
        'flex cursor-pointer items-center gap-2',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      <input
        type="radio"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="h-4 w-4 border-slate-300 text-brand-navy-600 focus:ring-brand-navy-300"
      />
      <span>{label}</span>
    </label>
  );
}
